/**
 * Appearance and language preferences.
 *
 * Theme, accent and background are the user's own and are saved on the server with
 * app.set_appearance (revision-checked); this device keeps a copy so the first paint
 * matches before the account loads. Background intensity and language are device-local
 * because the server stores neither. Nothing here changes other members' appearance.
 */
import { BACKGROUNDS, THEMES, type Appearance, type Background, type Theme } from '@miraops/contracts';
import { ACCENTS, DEFAULT_ACCENT } from './accents.ts';
import type { Locale } from './i18n.tsx';

export type { Theme, Background };
export const INTENSITIES = [0, 1, 2, 3] as const;
export type Intensity = typeof INTENSITIES[number];

export interface Prefs extends Appearance { intensity: Intensity; locale: Locale }

const KEY = 'miraops.prefs';
const HEX = /^#[0-9A-Fa-f]{6}$/;

function detectLocale(): Locale {
  const lang = (typeof navigator !== 'undefined' ? navigator.language : 'en').slice(0, 2);
  return lang === 'ru' || lang === 'ko' ? lang : 'en';
}

export const DEFAULT_APPEARANCE: Appearance = { theme: 'system', accent: ACCENTS[DEFAULT_ACCENT], background: 'default', reducedMotion: false };

export function loadPrefs(): Prefs {
  const fallback: Prefs = { ...DEFAULT_APPEARANCE, intensity: 0, locale: detectLocale() };
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return fallback;
    const p = JSON.parse(raw) as Partial<Prefs>;
    return {
      theme: (THEMES as readonly string[]).includes(p.theme ?? '') ? p.theme as Theme : 'system',
      accent: typeof p.accent === 'string' && HEX.test(p.accent) ? p.accent : fallback.accent,
      background: (BACKGROUNDS as readonly string[]).includes(p.background ?? '') ? p.background as Background : 'default',
      reducedMotion: p.reducedMotion === true,
      intensity: (INTENSITIES as readonly number[]).includes(p.intensity ?? -1) ? p.intensity as Intensity : 0,
      locale: p.locale === 'ru' || p.locale === 'ko' || p.locale === 'en' ? p.locale : fallback.locale,
    };
  } catch { return fallback; }
}

export function savePrefs(prefs: Prefs) {
  try { localStorage.setItem(KEY, JSON.stringify(prefs)); } catch { /* private mode */ }
}

export function appearanceOf(prefs: Prefs): Appearance {
  return { theme: prefs.theme, accent: prefs.accent, background: prefs.background, reducedMotion: prefs.reducedMotion };
}

export function sameAppearance(a: Appearance, b: Appearance) {
  return a.theme === b.theme && a.accent.toLowerCase() === b.accent.toLowerCase() && a.background === b.background && a.reducedMotion === b.reducedMotion;
}

/** Applies preferences to the document root; also used for live preview. */
export function applyPrefs(prefs: Prefs) {
  const root = document.documentElement;
  if (prefs.theme === 'system') root.removeAttribute('data-theme'); else root.setAttribute('data-theme', prefs.theme);
  root.setAttribute('data-bg', prefs.background);
  root.style.setProperty('--accent', prefs.accent);
  root.style.setProperty('--bg-mix', `${40 + prefs.intensity * 20}%`);
  root.toggleAttribute('data-reduced-motion', prefs.reducedMotion);
  root.lang = prefs.locale;
}

/** Durable idempotency keys for commands (see contracts/commands.ts). */
export const pendingStore = {
  get(key: string) { try { return localStorage.getItem(key); } catch { /* ignored: browser storage blocked: default preferences are used */  return null; } },
  set(key: string, value: string) { try { localStorage.setItem(key, value); } catch { /* falls back to in-memory retry only */ } },
  delete(key: string) { try { localStorage.removeItem(key); } catch { /* ignore */ } },
};

/** Per-device UI conveniences (visible columns); never security or shared state. */
export function loadLocal<T>(key: string, fallback: T, valid: (v: unknown) => v is T): T {
  try { const raw = localStorage.getItem(key); if (raw) { const v: unknown = JSON.parse(raw); if (valid(v)) return v; } } catch { /* unavailable */ }
  return fallback;
}
export function saveLocal(key: string, value: unknown) {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* unavailable */ }
}
